import { Button } from '@amore/ui/components/button'
import { Input } from '@amore/ui/components/input'
import { Plus, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { Task } from '@/mock/types'

export type TaskDraft = Pick<
  Task,
  'title' | 'note' | 'dueDate' | 'priority' | 'assigneeId' | 'labels'
>

export const emptyDraft: TaskDraft = {
  title: '',
  note: '',
  dueDate: null,
  priority: 'normal',
  assigneeId: null,
  labels: [],
}

export function draftOf(task: Task): TaskDraft {
  return {
    title: task.title,
    note: task.note,
    dueDate: task.dueDate,
    priority: task.priority,
    assigneeId: task.assigneeId,
    labels: task.labels,
  }
}

export function sameDraft(a: TaskDraft, b: TaskDraft) {
  return (
    a.title === b.title &&
    a.note === b.note &&
    a.dueDate === b.dueDate &&
    a.priority === b.priority &&
    a.assigneeId === b.assigneeId &&
    a.labels.join('|') === b.labels.join('|')
  )
}

export const datePickerLabels = {
  placeholder: 'Bez termínu',
  title: 'Termín úkolu',
  close: 'Zavřít kalendář',
  previousMonth: 'Předchozí měsíc',
  nextMonth: 'Následující měsíc',
  today: 'Dnes',
  clear: 'Bez termínu',
  weekdays: ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'],
}

/** "Štítky": the wedding's labels as toggle chips, plus a custom one. */
export function LabelPicker({
  value,
  available,
  readOnly,
  onChange,
  compact = false,
}: {
  value: string[]
  available: string[]
  readOnly: boolean
  onChange: (labels: string[]) => void
  /** Inside a property row: no heading, smaller controls. */
  compact?: boolean
}) {
  const [custom, setCustom] = useState<string | null>(null)
  const newLabelRef = useRef<HTMLInputElement>(null)
  const naming = custom !== null
  useEffect(() => {
    if (naming) newLabelRef.current?.focus()
  }, [naming])
  const options = [...new Set([...available, ...value])]
  const shown = readOnly ? value : options
  const toggle = (label: string) =>
    onChange(value.includes(label) ? value.filter((item) => item !== label) : [...value, label])
  const commit = () => {
    const label = custom?.trim()
    if (label && !value.includes(label)) onChange([...value, label])
    setCustom(null)
  }
  return (
    <div className="space-y-2.5">
      {compact ? null : <p className="text-sm font-semibold">Štítky</p>}
      {readOnly && shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">Bez štítků</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {shown.map((label) => {
            const selected = value.includes(label)
            return (
              <Button
                key={label}
                size="sm"
                appearance="outline"
                tone="neutral"
                aria-pressed={selected}
                onClick={() => toggle(label)}
              >
                {label}
                {selected && !readOnly ? <X className="size-3.5" aria-hidden="true" /> : null}
              </Button>
            )
          })}
          {readOnly ? null : custom === null ? (
            <Button
              size="sm"
              appearance="outline"
              tone="neutral"
              onClick={() => setCustom('')}
              className="border-dashed text-muted-foreground"
            >
              <Plus className="size-3.5" aria-hidden="true" /> Nový štítek
            </Button>
          ) : (
            <Input
              ref={newLabelRef}
              value={custom}
              maxLength={40}
              placeholder="Název štítku"
              aria-label="Nový štítek"
              className="h-9 w-40 rounded-full px-3.5 text-sm"
              onChange={(event) => setCustom(event.target.value)}
              onBlur={commit}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  commit()
                }
                if (event.key === 'Escape') {
                  event.preventDefault()
                  event.stopPropagation()
                  setCustom(null)
                }
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
